'use client';

import { useEffect, useId, useCallback } from 'react';

const VIDEO_PLAY_EVENT = 'smartvideo:play';

/**
 * Global video pause coordination.
 * When one video starts playing, all others pause automatically.
 * Uses a CustomEvent on window so it works across all component instances.
 */
export function useGlobalVideoPause(
  onPause: () => void
) {
  const instanceId = useId();

  const notifyPlay = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent(VIDEO_PLAY_EVENT, { detail: instanceId })
    );
  }, [instanceId]);

  useEffect(() => {
    const handler = (e: Event) => {
      const playingId = (e as CustomEvent<string>).detail;
      if (playingId !== instanceId) {
        onPause();
      }
    };

    window.addEventListener(VIDEO_PLAY_EVENT, handler);
    return () => { window.removeEventListener(VIDEO_PLAY_EVENT, handler); };
  }, [onPause, instanceId]);

  return { notifyPlay };
}
